<template>
  <section>
    <div class="px-4 py-4 mx-auto max-w-3xl lg:px-0 lg:py-6">
      <div class="flex flex-col gap-3 w-full">
        <div class="inline-flex items-start px-3.5 py-2 w-fit rounded border border-[#E0D5C5] bg-[#F0E8D8]">
          <span
            class="text-xs tracking-wide text-[#6B3A22] uppercase font-ww-regular"
            v-html="badgeText"
          />
        </div>

        <h2
          class="text-22 font-bold leading-27 font-ww-bold text-[#3B2314]"
          v-html="title"
        />

        <div
          class="mt-1 pt-0.5 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5"
          v-html="introContent"
        />

        <ul class="grid grid-cols-1 gap-2.5 pt-2 pb-6 mt-2 sm:grid-cols-2">
          <li
            v-for="(benefit, index) in benefits"
            :key="index"
            class="flex gap-2.5 items-start px-4 py-3 min-h-14 rounded-md border border-[#E0D5C5] bg-[#FAF6F0]"
          >
            <span class="text-sm leading-6 shrink-0 text-[#F1574B] font-ww-regular">✓</span>
            <span
              class="text-15 leading-7 text-[#4A4A4A] font-ww-regular"
              v-html="benefit.text"
            />
          </li>
        </ul>

        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-12 w-full lg:flex-row lg:items-start lg:gap-12">
            <div class="relative mx-auto w-full max-w-xs shrink-0 lg:mx-0">
              <nuxt-img
                v-if="imageSrc"
                :src="imageSrc"
                :alt="imageAlt"
                :width="imageWidth"
                :height="imageHeight"
                format="webp"
                loading="lazy"
                sizes="xs:100vw sm:320px lg:320px"
                class="object-cover w-full rounded-2xl aspect-[320/446]"
              />
              <div
                v-else
                class="w-full rounded-2xl aspect-[320/446] bg-[#2A2A2A]"
                aria-hidden="true"
              />
            </div>

            <div
              class="flex-1 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_em]:italic [&_strong]:font-ww-bold [&_sup]:text-xs"
              v-html="bodyContent"
            />
          </div>

          <div
             v-if="quoteContent"
            class="py-5 pr-6 pl-7 w-full rounded-r-md border-l-4 border-[#2D5A27] bg-[#E8F0E6]"
          >
            <div
              class="text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_sup]:text-xs"
              v-html="quoteContent"
            />
          </div>
        </div>

        <div class="mt-10 w-full h-px bg-lifeboost-gray"></div>

      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  badgeText: {
    type: String,
    default: 'Supplement Focus'
  },
  title: {
    type: String,
    default: 'Nattokinase: Nature\'s Blood Vessel Cleaner'
  },
  introContent: {
    type: String,
    default: `<p>Nattokinase is a natural supplement made from a traditional Japanese food, natto, made from fermented soybeans.</p>
<p>Research shows nattokinase can:</p>`
  },
  benefits: {
    type: Array,
    default: () => [
      { text: 'Support the break down fibrin (a protein involved in clot formation)' },
      { text: 'Support reducing blood pressure' },
      { text: 'Support healthy blood viscosity' },
      { text: 'Support arterial health' },
      { text: 'Support improved circulation' }
    ]
  },
  imageSrc: {
    type: String,
    default: '/assets/images/left-mens-multiple.jpg'
  },
  imageAlt: {
    type: String,
    default: 'Fermented soybeans and natto ingredients'
  },
  imageWidth: {
    type: Number,
    default: 320
  },
  imageHeight: {
    type: Number,
    default: 446
  },
  bodyContent: {
    type: String,
    default: `<p>A 2022 review in <em>Frontiers in Cardiovascular Medicine</em> concluded that nattokinase has <strong>multiple cardiovascular-protective effects</strong> and may help reduce the risk of hypertension and stroke.<sup>10</sup></p>
<p>Nattokinase, as a single compound, in some cases, has been shown to not only possess multiple cardiovascular disease preventative effects, it also can alleviate unwanted pharmacological effects commonly experienced when using traditional meds.</p>
<p>A review of multiple nattokinase studies concluded the following - "there are no other drugs or drug candidates with multiple pharmacologic properties similar to nattokinase." <sup>12</sup></p>
<p>For men concerned about heart health — especially as they age — nattokinase has the potential to be one of the most promising natural tools available</p>`
  },
  quoteContent: {
    type: String,
    default: `<p>Dr. Kevin Wang, associate professor of molecular biology at Northeastern State University, is one of the leading researchers of nattokinase - here's what he has to say about this powerful supplement - "nattokinase has the ability to get into the bloodstream and can directly destroy blood clots, thin the blood, and improve blood flow…it cleans out the blood vessels, reducing the risk of hypertension and stroke and can also reduce the risk of heart attack." <sup>11</sup></p>`
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Supplement Focus Nattokinase',
  inputs: [
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Supplement Focus'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'Nattokinase: Nature\'s Blood Vessel Cleaner'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Nattokinase is a natural supplement made from a traditional Japanese food, natto, made from fermented soybeans.</p>
<p>Research shows nattokinase can:</p>`
    },
    {
      name: 'benefits',
      type: 'list',
      defaultValue: [
        { text: 'Support the break down fibrin (a protein involved in clot formation)' },
        { text: 'Support reducing blood pressure' },
        { text: 'Support healthy blood viscosity' },
        { text: 'Support arterial health' },
        { text: 'Support improved circulation' }
      ],
      subFields: [
        {
          name: 'text',
          type: 'text',
          defaultValue: 'Benefit text'
        }
      ]
    },
    {
      name: 'imageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/left-mens-multiple.jpg'
    },
    {
      name: 'imageAlt',
      type: 'text',
      defaultValue: 'Fermented soybeans and natto ingredients'
    },
    {
      name: 'imageWidth',
      type: 'number',
      defaultValue: 320
    },
    {
      name: 'imageHeight',
      type: 'number',
      defaultValue: 446
    },
    {
      name: 'bodyContent',
      type: 'richText',
      defaultValue: `<p>A 2022 review in <em>Frontiers in Cardiovascular Medicine</em> concluded that nattokinase has <strong>multiple cardiovascular-protective effects</strong> and may help reduce the risk of hypertension and stroke.<sup>10</sup></p>
<p>Nattokinase, as a single compound, in some cases, has been shown to not only possess multiple cardiovascular disease preventative effects, it also can alleviate unwanted pharmacological effects commonly experienced when using traditional meds.</p>
<p>A review of multiple nattokinase studies concluded the following - "there are no other drugs or drug candidates with multiple pharmacologic properties similar to nattokinase." <sup>12</sup></p>
<p>For men concerned about heart health — especially as they age — nattokinase has the potential to be one of the most promising natural tools available</p>`
    },
    {
      name: 'quoteContent',
      type: 'richText',
      defaultValue: `<p>Dr. Kevin Wang, associate professor of molecular biology at Northeastern State University, is one of the leading researchers of nattokinase - here's what he has to say about this powerful supplement - "nattokinase has the ability to get into the bloodstream and can directly destroy blood clots, thin the blood, and improve blood flow…it cleans out the blood vessels, reducing the risk of hypertension and stroke and can also reduce the risk of heart attack." <sup>11</sup></p>`
    }
  ]
})
</script>
